/** Shared wholesale product helpers — UOM, trade tiers, EAN-13, pricing resolution */

export type UomUnit = {
  code: string;
  label: string;
  factor: number;
  price?: number | null;
  barcode?: string;
};

export type PricingTier = 'A' | 'B' | 'C';

export interface WholesaleProductLike {
  price?: number | string | null;
  wholesale_price?: number | string | null;
  tier_a_price?: number | string | null;
  tier_b_price?: number | string | null;
  tier_c_price?: number | string | null;
  case_price?: number | string | null;
  case_quantity?: number | string | null;
  box_quantity?: number | string | null;
  unit?: string | null;
  uom_units?: UomUnit[] | string | null;
}

export function toNum(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function parseUomUnits(raw: unknown): UomUnit[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map(u => ({
        code: String(u.code || '').trim(),
        label: String(u.label || u.code || '').trim(),
        factor: toNum(u.factor, 0),
        price: u.price != null && u.price !== '' ? toNum(u.price) : null,
        barcode: u.barcode ? String(u.barcode).trim() : '',
      }))
      .filter(u => u.code && u.factor > 0);
  }
  if (typeof raw === 'string') {
    try { return parseUomUnits(JSON.parse(raw)); } catch { return []; }
  }
  return [];
}

/** EAN-13 check digit (GS1) for first 12 digits */
export function ean13CheckDigit(twelveDigits: string): string {
  const d = twelveDigits.replace(/\D/g, '').slice(0, 12).padStart(12, '0');
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(d[i], 10) * (i % 2 === 0 ? 1 : 3);
  }
  return String((10 - (sum % 10)) % 10);
}

/**
 * Generate a valid EAN-13 barcode.
 * Uses prefix 505 (UK GS1 range) + 9-digit product seed from id/timestamp.
 */
export function generateEan13(seed?: string | number): string {
  const n = seed != null
    ? String(seed).replace(/\D/g, '').slice(-9).padStart(9, '0')
    : String(Date.now()).slice(-9);
  const base12 = `505${n}`.slice(0, 12).padEnd(12, '0');
  return base12 + ean13CheckDigit(base12);
}

export function isValidEan13(code: string): boolean {
  const c = code.replace(/\s/g, '');
  if (!/^\d{13}$/.test(c)) return false;
  return c[12] === ean13CheckDigit(c.slice(0, 12));
}

export function resolveTradeUnitPrice(
  product: WholesaleProductLike,
  opts?: {
    pricingTier?: PricingTier | string | null;
    isWholesale?: boolean;
    contractPrice?: number | null;
  }
): number {
  if (opts?.contractPrice != null && opts.contractPrice >= 0) return opts.contractPrice;

  const tier = opts?.pricingTier;
  if (tier === 'A' && toNum(product.tier_a_price) > 0) return toNum(product.tier_a_price);
  if (tier === 'B' && toNum(product.tier_b_price) > 0) return toNum(product.tier_b_price);
  if (tier === 'C' && toNum(product.tier_c_price) > 0) return toNum(product.tier_c_price);

  if (opts?.isWholesale && toNum(product.wholesale_price) > 0) return toNum(product.wholesale_price);
  return toNum(product.price);
}

export function getCaseQuantity(product: WholesaleProductLike): number {
  return toNum(product.case_quantity) || toNum(product.box_quantity) || 0;
}

export function resolveCasePrice(
  product: WholesaleProductLike,
  unitPrice?: number,
  opts?: Parameters<typeof resolveTradeUnitPrice>[1]
): number | null {
  if (toNum(product.case_price) > 0) return toNum(product.case_price);
  const cq = getCaseQuantity(product);
  if (!cq) return null;
  const up = unitPrice ?? resolveTradeUnitPrice(product, opts);
  return up > 0 ? up * cq : null;
}

export function resolveUomPrice(
  product: WholesaleProductLike,
  uomCode: string,
  opts?: Parameters<typeof resolveTradeUnitPrice>[1]
): number | null {
  const units = parseUomUnits(product.uom_units);
  const u = units.find(x => x.code === uomCode);
  if (!u) return null;
  if (u.price != null && u.price > 0) return u.price;
  const base = resolveTradeUnitPrice(product, opts);
  return base > 0 ? base * u.factor : null;
}

/** Category-based smart defaults for UK wholesale */
export const CATEGORY_DEFAULTS: Record<string, { taxRate?: number; unit?: string; countryOfOrigin?: string }> = {
  food: { taxRate: 0, unit: 'kg', countryOfOrigin: 'United Kingdom' },
  grocery: { taxRate: 0, unit: 'kg', countryOfOrigin: 'United Kingdom' },
  beverages: { taxRate: 20, unit: 'l', countryOfOrigin: 'United Kingdom' },
  alcohol: { taxRate: 20, unit: 'l', countryOfOrigin: 'United Kingdom' },
  dairy: { taxRate: 0, unit: 'kg', countryOfOrigin: 'United Kingdom' },
  frozen: { taxRate: 0, unit: 'kg', countryOfOrigin: 'United Kingdom' },
  household: { taxRate: 20, unit: 'pcs', countryOfOrigin: 'United Kingdom' },
};

export function smartDefaultsForCategory(category?: string) {
  if (!category) return {};
  const key = category.toLowerCase();
  for (const [k, v] of Object.entries(CATEGORY_DEFAULTS)) {
    if (key.includes(k)) return v;
  }
  return { taxRate: 20, unit: 'pcs', countryOfOrigin: 'United Kingdom' };
}

export const UOM_PRESETS: { code: string; label: string; factor: number }[] = [
  { code: 'each', label: 'Each (base)', factor: 1 },
  { code: 'box', label: 'Box / Outer', factor: 12 },
  { code: 'case', label: 'Case', factor: 24 },
  { code: 'layer', label: 'Layer', factor: 48 },
  { code: 'pallet', label: 'Pallet', factor: 480 },
];

export const UK_ORIGIN_OPTIONS = [
  'United Kingdom',
  'England',
  'Scotland',
  'Wales',
  'Northern Ireland',
  'European Union',
  'China',
  'India',
  'Turkey',
  'Other',
];
